import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getEligibleActivities,
  claimActivity,
  type ActivityPublic,
} from '../api/fan-api';
import { getStoredFanClaims } from '../auth/fan-auth';
import { notifyWalletChanged } from '../lib/wallet-bus';

/**
 * Login popup that surfaces operator-defined campaigns the fan is
 * eligible for. Fires once per app mount (after sign-in). Stores
 * "seen" flag in sessionStorage so re-mounts within the same tab
 * don't keep re-showing.
 */
const SESSION_FLAG = 'embedFanActivityPopupShown';

export function ActivityPopup() {
  const navigate = useNavigate();
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const [queue, setQueue] = useState<ActivityPublic[]>([]);
  const [index, setIndex] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    let alive = true;
    (async () => {
      try {
        const out = await getEligibleActivities();
        if (alive && out.items.length > 0) {
          setQueue(out.items);
          sessionStorage.setItem(SESSION_FLAG, '1');
        }
      } catch {
        // Silent — popups are progressive enhancement, never block UX
      }
    })();
    return () => { alive = false; };
  }, [signedIn]);

  if (queue.length === 0 || index >= queue.length) return null;
  const current = queue[index]!;

  async function onClaim(): Promise<void> {
    setClaiming(true);
    setErr(null);
    try {
      const r = await claimActivity(current.activityId);
      if (r.claimed) {
        // Newly credited — push the HUD pill to refetch.
        notifyWalletChanged();
      }
      if (r.claimed || r.alreadyClaimed) {
        // Move to next; handle CTA on the last one
        const isLast = index >= queue.length - 1;
        if (current.popupConfig.ctaAction === 'topup') {
          navigate('/wallet');
          setQueue([]);
          return;
        }
        if (current.popupConfig.ctaAction === 'browse') {
          navigate('/');
          setQueue([]);
          return;
        }
        if (isLast) {
          setQueue([]);
        } else {
          setIndex(index + 1);
        }
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setClaiming(false);
    }
  }

  function onDismiss(): void {
    if (index >= queue.length - 1) {
      setQueue([]);
    } else {
      setIndex(index + 1);
    }
  }

  const rewardLabel =
    current.reward.type === 'platform-points'
      ? `${current.reward.amount} 平台點`
      : `${current.reward.amount} 群組點`;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="activity-popup-title"
    >
      <div
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
        onClick={() => { if (!claiming) onDismiss(); }}
        aria-hidden="true"
      />
      <div
        className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border-t sm:border border-violet-500/50 bg-slate-900 shadow-2xl p-5 sm:p-6"
        style={{ paddingBottom: `calc(1.25rem + env(safe-area-inset-bottom))` }}
      >
        {/* Drag-handle decoration on mobile bottom sheet */}
        <div className="sm:hidden absolute top-2 inset-x-0 flex justify-center pointer-events-none">
          <div className="h-1 w-10 rounded-full bg-slate-700" />
        </div>

        <div className="mt-2 sm:mt-0 flex items-start justify-between gap-3">
          <h2 id="activity-popup-title" className="text-lg sm:text-xl font-semibold flex-1 min-w-0">
            <span aria-hidden="true">🎁</span> {current.name}
          </h2>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="關閉"
            className="shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-800 text-xl leading-none focus:outline-none focus:ring-2 focus:ring-violet-500"
          >×</button>
        </div>

        {current.description && (
          <p className="text-slate-300 text-sm mt-2 whitespace-pre-wrap">{current.description}</p>
        )}

        {/* Reward emphasis — slightly bigger + sparkle to feel
            celebratory. The "獎勵" mini-label gets eyebrow styling. */}
        <div className="mt-4 rounded-xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 to-violet-500/5 p-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-300">獎勵</div>
          <div className="text-2xl sm:text-3xl font-bold text-violet-200 mt-1 tabular-nums">
            <span aria-hidden="true">✨</span> +{rewardLabel}
          </div>
        </div>

        {err && (
          <div role="alert" className="mt-3 rounded-lg bg-rose-500/15 border border-rose-500/40 px-3 py-2 text-sm text-rose-200">
            {err}
          </div>
        )}

        {/* Stack on mobile, side-by-side on sm. Primary CTA is the
            "領取" — sized to dominate when wider. */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => { void onClaim(); }}
            disabled={claiming}
            className="sm:flex-[2] rounded-lg bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium py-3 transition focus:outline-none focus:ring-2 focus:ring-violet-300 active:scale-[0.99]"
          >
            {claiming ? '領取中…' : (current.popupConfig.ctaLabel || '立即領取')}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="sm:flex-1 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-200 py-3 transition focus:outline-none focus:ring-2 focus:ring-slate-500"
          >稍後再說</button>
        </div>

        {queue.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-1.5" aria-label={`第 ${index + 1} 個，共 ${queue.length} 個獎勵`}>
            {/* Pagination dots — clearer than "1 / 3" for a small
                queue. Active dot is indigo + wider; rest are slate
                pills. */}
            {queue.map((_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-4 bg-violet-400' : 'w-1.5 bg-slate-700'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
